// GraphQL field declaration for the activeOrdersFilters query, passed into useDataModel's
// `dataModel` option — mirrors activeOrdersDataModel.ts's structure. Confirmed against
// order-management-svc's schema.graphqls (2026-09-22, github.com/one-thd/order-management-svc),
// replacing the earlier hand-rolled REST placeholder (see git history of
// useActiveOrderFilterOptionsQuery.ts and ADR-003's "Future work" item on this endpoint):
//
//   activeOrdersFilters(userId: ID!, headers: HeaderExtras, request: ActiveOrdersFilterRequest!):
//     ActiveOrdersFilterResponseWrapper!
//
//   input ActiveOrdersFilterRequest {
//     customerAccountId: String!
//     distributionDate: String
//     filterTypes: [String]
//   }
//
//   type ActiveOrdersFilterResponseWrapper { activeOrdersFilterResponse: ActiveOrdersFilterResponse }
//   type ActiveOrdersFilterResponse { project: [String], poJobName: [String] }
//
// Two differences from activeOrders:
// * Takes a top-level `userId: ID!` (mirroring homepage/purchaseHistory's own `userId` arg) —
//   activeOrders has no such argument. `purchaserId` (this component's identity prop, ADR-003) is
//   sent as `userId` here, the same purchaserId-as-identity assumption ADR-003 documents for the
//   sibling activeOrders query.
// * `ActiveOrdersFilterRequest.customerAccountId` is `String!`, not `ID!` (same schema fetch) — so
//   it's typed with `string()` below rather than `id()`.
//
// distributionDate is REQUIRED at runtime by order-management-svc's
// ActiveOrdersFilterRequestValidator (confirmed 2026-09-21 — a missing/invalid value 400s with
// ACTIVE_ORDERS_FILTERS_INVALID_REQUEST_INVALID_DISTRIBUTION_DATE) despite being nullable in the
// schema. useActiveOrderFilterOptionsQuery.ts always sends `'ALL'`: Project/PO-Job Name options
// are a global facet universe, not scoped to the table's currently-selected date range.
import { params, shape, arrayOf, string, id, customType } from '@thd-nucleus/data-sources';

export interface ActiveOrderFilterOptionsQueryVariables {
  userId: string;
  headers?: {
    tmxProfileId?: string;
    channelId?: string;
  };
  request: {
    customerAccountId: string;
    distributionDate?: string;
    filterTypes?: string[];
  };
}

export const activeOrderFilterOptionsDataModel = {
  activeOrdersFilters: params({
    userId: id().isRequired(),
    headers: customType('HeaderExtras').shape({ tmxProfileId: string(), channelId: string() }),
    request: customType('ActiveOrdersFilterRequest!').shape({
      customerAccountId: string().isRequired(),
      distributionDate: string(),
      filterTypes: arrayOf(string())
    })
  }).shape({
    activeOrdersFilterResponse: shape({
      project: arrayOf(string()),
      poJobName: arrayOf(string())
    })
  })
};
